/**
 * `<ResourceMatrix>` — a checkbox grid of resources (rows) × permission verbs
 * (columns). Used inside the role editor's "Specific workflows / queries" mode
 * (rbac-permissions §3.2).
 */

import { Checkbox } from "@cometchat-ai/ui-dashboard";

export function ResourceMatrix({
  rows,
  cols,
  emptyLabel,
  isChecked,
  onToggle,
}: {
  rows: Array<{ id: string; name: string }>;
  cols: Array<{ key: string; label: string }>;
  emptyLabel: string;
  isChecked: (id: string, col: string) => boolean;
  onToggle: (id: string, col: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-muted-foreground">
        {emptyLabel}
      </p>
    );
  }
  const template = `1fr repeat(${cols.length}, 4rem)`;
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div
        className="grid items-center gap-2 bg-muted px-4 py-2 text-xs font-medium text-muted-foreground"
        style={{ gridTemplateColumns: template }}
      >
        <span />
        {cols.map((c) => (
          <span key={c.key} className="text-center">
            {c.label}
          </span>
        ))}
      </div>
      <div className="max-h-64 divide-y divide-border overflow-y-auto">
        {rows.map((row) => (
          <div
            key={row.id}
            className="grid items-center gap-2 px-4 py-2.5 text-[13px]"
            style={{ gridTemplateColumns: template }}
            data-testid={`resource-row-${row.id}`}
          >
            <span className="truncate text-foreground">{row.name}</span>
            {cols.map((c) => (
              <span key={c.key} className="flex justify-center">
                <Checkbox
                  checked={isChecked(row.id, c.key)}
                  onCheckedChange={() => onToggle(row.id, c.key)}
                  className="h-4 w-4 rounded-[5px]"
                  aria-label={`${row.name} ${c.label}`}
                  data-testid={`perm-${row.id}-${c.key}`}
                />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
